import type { ItemDef, ItemKind } from "../schema/item.js";

function item(id: string, kind: ItemKind, name: [string, string], refPrice: number, params: ItemDef["params"] = {}, stackable = true): ItemDef {
  return { id, kind, name: { de: name[0], en: name[1] }, params, refPrice, stackable };
}

export const SPEEDUP_TYPES = ["general", "build", "research", "repair", "fabrication"] as const;
export type SpeedupType = (typeof SPEEDUP_TYPES)[number];
export const SPEEDUP_MINUTES = [1, 5, 15, 60, 180, 480, 1440, 4320] as const;

const SPEEDUP_TYPE_NAMES: Record<SpeedupType, [string, string]> = {
  general: ["Allgemein", "General"],
  build: ["Bau", "Build"],
  research: ["Forschung", "Research"],
  repair: ["Reparatur", "Repair"],
  fabrication: ["Fabrikation", "Fabrication"],
};

function minutesLabel(m: number): string {
  if (m >= 1440) return `${m / 1440}d`;
  if (m >= 60) return `${m / 60}h`;
  return `${m}m`;
}

const speedups: ItemDef[] = SPEEDUP_TYPES.flatMap((type) =>
  SPEEDUP_MINUTES.map((m) =>
    item(`itm.speedup_${type}_${m}`, "speedup", [`Beschleunigung ${minutesLabel(m)} (${SPEEDUP_TYPE_NAMES[type][0]})`, `Speedup ${minutesLabel(m)} (${SPEEDUP_TYPE_NAMES[type][1]})`], m, { type, minutes: m }),
  ),
);

const CRATE_SIZES: { suffix: string; amount: number }[] = [
  { suffix: "1k", amount: 1_000 },
  { suffix: "10k", amount: 10_000 },
  { suffix: "100k", amount: 100_000 },
  { suffix: "1m", amount: 1_000_000 },
];
const CRATE_VALUE_PER_K: Record<string, number> = { RAT: 1, POL: 1, ALY: 2, ENC: 5 };
const RESOURCE_NAMES: Record<string, [string, string]> = { RAT: ["Rationen", "Rations"], POL: ["Polymer", "Polymer"], ALY: ["Legierung", "Alloy"], ENC: ["Energiezellen", "Energy Cells"] };

const crates: ItemDef[] = ["RAT", "POL", "ALY", "ENC"].flatMap((res) =>
  CRATE_SIZES.map((c) =>
    item(`itm.crate_${res.toLowerCase()}_${c.suffix}`, "resource_crate", [`${RESOURCE_NAMES[res]![0]}-Kiste ${c.suffix}`, `${RESOURCE_NAMES[res]![1]} Crate ${c.suffix}`], (c.amount / 1000) * CRATE_VALUE_PER_K[res]!, { resource: res, amount: c.amount }),
  ),
);

const xp: ItemDef[] = [500, 2000, 10000, 50000].map((n) => item(`itm.xp_${n}`, "xp", [`Trainingsdaten ${n}`, `Training Data ${n}`], n / 500, { xp: n }));

export const ITEMS: readonly ItemDef[] = [
  ...speedups,
  ...crates,
  ...xp,
  item("itm.cdc", "cdc", ["Komprimierter Datenkern", "Compacted Data Core"], 3),
  item("itm.fab", "fab", ["Fabrikationsmarke", "Fabrication Token"], 150),
  item("itm.ffab", "ffab", ["Featured-Fabrikationsmarke", "Featured Fabrication Token"], 250),
  item("itm.stamina_20", "stamina", ["Treibstoff +20", "Fuel +20"], 30, { amount: 20 }),
  item("itm.stamina_50", "stamina", ["Treibstoff +50", "Fuel +50"], 70, { amount: 50 }),
  item("itm.stamina_100", "stamina", ["Treibstoff +100", "Fuel +100"], 130, { amount: 100 }),
  item("itm.shield_8h", "shield", ["Schutzschild 8h", "Shield 8h"], 60, { hours: 8 }),
  item("itm.shield_24h", "shield", ["Schutzschild 24h", "Shield 24h"], 150, { hours: 24 }),
  item("itm.shield_3d", "shield", ["Schutzschild 3d", "Shield 3d"], 400, { hours: 72 }),
  item("itm.shield_7d", "shield", ["Schutzschild 7d", "Shield 7d"], 800, { hours: 168 }),
  item("itm.teleport_random", "teleport", ["Zufallsteleport", "Random Teleport"], 200, { mode: "random" }),
  item("itm.teleport_advanced", "teleport", ["Zielteleport", "Advanced Teleport"], 600, { mode: "advanced" }),
  item("itm.ufr_s", "ufr", ["Universalfragment S", "Universal Fragment S"], 60, { tier: "S" }),
  item("itm.ufr_a", "ufr", ["Universalfragment A", "Universal Fragment A"], 25, { tier: "A" }),
  item("itm.ufr_b", "ufr", ["Universalfragment B", "Universal Fragment B"], 10, { tier: "B" }),
  item("itm.ufr_c", "ufr", ["Universalfragment C", "Universal Fragment C"], 4, { tier: "C" }),
  item("itm.ufr_d", "ufr", ["Universalfragment D", "Universal Fragment D"], 2, { tier: "D" }),
  /** Per-unit fragments are stored as `itm.fragment:<unitId>` in inventories. */
  item("itm.fragment", "fragment", ["Blaupausenfragment", "Blueprint Fragment"], 5),
  item("itm.nano", "nano", ["Nano-Kit", "Nano-Kit"], 2),
  /** Module blueprints are stored as `itm.blueprint:<moduleId>`. */
  item("itm.blueprint", "blueprint", ["Modulblaupause", "Module Blueprint"], 40),
  item("itm.drone_core", "drone_core", ["Drohnenkern", "Drone Core"], 8),
  item("itm.base_skin_fragment", "skin", ["Basislack-Fragment", "Base Paint Fragment"], 0, { required: 6 }),
  item("itm.sweep", "sweep", ["Sweep-Ticket", "Sweep Ticket"], 4),
  item("itm.chest_bronze", "chest", ["Bronzetruhe", "Bronze Chest"], 50, { table: "chest_bronze" }),
  item("itm.chest_silver", "chest", ["Silbertruhe", "Silver Chest"], 200, { table: "chest_silver" }),
  item("itm.chest_gold", "chest", ["Goldtruhe", "Gold Chest"], 800, { table: "chest_gold" }),
  // D70: decorations are items; placing consumes one, storing returns it (the garden is bought for SCR and has no item drop).
  item("itm.deco_garden", "decoration", ["Gartenkuppel", "Garden Dome"], 100, { decoId: "deco.garden" }),
  item("itm.deco_banner", "decoration", ["Banner", "Banner"], 60, { decoId: "deco.banner" }),
  item("itm.deco_floodlight", "decoration", ["Flutlicht", "Floodlight"], 60, { decoId: "deco.floodlight" }),
  item("itm.deco_floor_tile", "decoration", ["Bodenplatte", "Floor Tile"], 40, { decoId: "deco.floor_tile" }),
  item("itm.deco_holo_sign", "decoration", ["Holo-Schild", "Holo Sign"], 150, { decoId: "deco.holo_sign" }),
  item("itm.deco_statue", "decoration", ["Statue", "Statue"], 400, { decoId: "deco.statue" }),
  item("itm.deco_elm", "decoration", ["Ulme", "Elm"], 60, { decoId: "deco.elm" }),
  item("itm.deco_yard_water", "decoration", ["Wasserhof", "Water Yard"], 250, { decoId: "deco.yard_water" }),
  item("itm.deco_yard_supply", "decoration", ["Versorgungshof", "Supply Yard"], 250, { decoId: "deco.yard_supply" }),
  item("itm.deco_yard_service", "decoration", ["Servicehof", "Service Yard"], 600, { decoId: "deco.yard_service" }),
  item("itm.token_arena", "token", ["Arenamünze", "Arena Coin"], 1),
  item("itm.token_prestige", "token", ["Prestigemarke", "Prestige Mark"], 1),
  item("itm.token_salvage", "token", ["Bergungsmarke", "Salvage Token"], 1),
  item("itm.token_event", "token", ["Eventmarke", "Event Token"], 1),
  item("itm.alliance_coin", "token", ["Allianzmünze", "Alliance Coin"], 1),
  /** Skins are stored as `itm.skin:<skinId>` and are not stackable. */
  item("itm.skin", "skin", ["Skin", "Skin"], 500, {}, false),
];

const itemMap = new Map(ITEMS.map((i) => [i.id, i]));

/** Resolves `itm.fragment:unit.x` style ids to their base definition. */
export function itemById(id: string): ItemDef {
  const base = id.includes(":") ? id.slice(0, id.indexOf(":")) : id;
  const i = itemMap.get(base);
  if (!i) throw new Error(`unknown item ${id}`);
  return i;
}

export function hasItem(id: string): boolean {
  const base = id.includes(":") ? id.slice(0, id.indexOf(":")) : id;
  return itemMap.has(base);
}

export function speedupItemId(type: SpeedupType, minutes: (typeof SPEEDUP_MINUTES)[number]): string {
  return `itm.speedup_${type}_${minutes}`;
}

export function fragmentItemId(unitId: string): string {
  return `itm.fragment:${unitId}`;
}
